/** 应用实例 API detail → innerForm（编辑回填） */
import { cloneDeep, get } from 'lodash';

import { healthCheckFromLivenessProbe, parseHealthCheckFromDeployConfig } from '@/delivery-type-plugin/capabilities/health-check';
import { readGroupSystemRegistry } from '@/delivery-type-plugin/capabilities/image';
import { syncInferenceServiceWithNetworkStatus } from '@/delivery-type-plugin/capabilities/maas-init';
import type { MaasFramework, MaasWorkload } from '@/delivery-type-plugin/capabilities/maas-init';
import { MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource, normalizeMaasRuntime } from '@/delivery-type-plugin/capabilities/maas-init';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  buildStorageClassNameOriginalFromInfo,
  resolveStorageClassFromVolumeStatus,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { stripInitCommandFromNodesJson } from '@/delivery-type-plugin/capabilities/maas-node-groups/initCommandStartup';
import { resolveMaasInferenceSourceForForm } from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { resolveContainerDeployConfigFromFormValues } from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import {
  SERVICE_MOUNT_GROUP_NAME_FIELD,
  deserializeServiceListFromApi,
  resolveServiceMountGroupNamesFromServices,
} from '@/delivery-type-plugin/capabilities/service';
import { hasModulePresetData } from '@/delivery-type-plugin/platform/scene-layout';
import {
  applyMaasFormSnapshotToFormValues,
  buildSystemDiskFormFields,
  extractSystemDiskSizesFromGroups,
  getFrameworkFromWorkload,
  getPrimaryGroupFromValues,
  getVolumeMountPath,
  parseDetailGroupsToNodeFormFields,
  readMaasFormAnnotationSnapshot,
  resolveRunModeFromWorkload,
} from '@/delivery-type-plugin/plugins';

import { type AppStorageGroup, buildAppStorageGroupsFromDetailVolumes, findAllDataVolumesInGroup } from './headless/appStorageGroups';
import { APP_INSTANCE_SKU_MODULE_META } from './resourceConfigs';
import type { ApplicationInstanceFormValues, ApplicationInstanceSpec } from './types';
import { DEFAULT_APP_DATA_MOUNT_PATH } from './types';

/** 按表单数据把 envVars / service 勾选打开（对齐 CI / TC） */
export function enrichAppInstanceSkuResourceModules(
  data: Record<string, unknown>,
  modules?: Record<string, boolean>,
): Record<string, boolean> | undefined {
  const hasEnv = hasModulePresetData(data, APP_INSTANCE_SKU_MODULE_META.envVars);
  const hasService = hasModulePresetData(data, APP_INSTANCE_SKU_MODULE_META.service);
  if (!modules && !hasEnv && !hasService) return undefined;
  return {
    ...(modules ?? {}),
    ...(hasEnv ? { envVars: true } : {}),
    ...(hasService ? { service: true } : {}),
  };
}

/** 编辑回填：按卷 name 从 status.volumeStatus 写入 storageClassNameOriginal */
function applyVolumeStatusStorageClassToGroups(detail: MaasWorkload, groups: AppStorageGroup[]): AppStorageGroup[] {
  return groups.map((group) => {
    const vol = group.volume;
    const volName = String(vol?.name ?? '').trim();
    if (!vol || !volName) return group;
    const storageClassInfo = resolveStorageClassFromVolumeStatus(detail, { name: volName });
    if (!storageClassInfo) return group;
    return {
      ...group,
      volume: {
        ...vol,
        storageClassNameOriginal: buildStorageClassNameOriginalFromInfo(storageClassInfo),
      },
    };
  });
}

/** 从顶层 spec.network.service 回填表单服务列表（与模型实例一致） */
function resolveInferenceServiceForForm(spec: ApplicationInstanceSpec): unknown {
  const topLevel = Array.isArray(spec.network?.service) ? spec.network.service : undefined;
  if (!topLevel?.length) return undefined;
  return deserializeServiceListFromApi(topLevel);
}

function resolveServiceMountGroupNamesForForm(spec: ApplicationInstanceSpec): string[] | undefined {
  const topLevel = Array.isArray(spec.network?.service) ? spec.network.service : undefined;
  const groupNames = resolveServiceMountGroupNamesFromServices(topLevel);
  return groupNames.length ? groupNames : undefined;
}

/** API Workload → 应用实例表单值 */
export function hydrateApplicationInstanceDetailToForm(detail: MaasWorkload): ApplicationInstanceFormValues {
  const formSnapshot = readMaasFormAnnotationSnapshot(detail);
  const commonEnvFromAnnotation = formSnapshot.commonEnv;

  const spec = cloneDeep(detail.spec || {}) as ApplicationInstanceSpec;
  const runMode = resolveRunModeFromWorkload(detail);
  const framework = (getFrameworkFromWorkload(detail) as MaasFramework | undefined) || spec.framework;

  const primaryGroup = getPrimaryGroupFromValues({ ...spec, runMode }, runMode);
  const hydratedRegistry =
    readGroupSystemRegistry(primaryGroup) ??
    readGroupSystemRegistry(spec.headGroupSpec) ??
    readGroupSystemRegistry(spec.workerGroupSpecs?.[0]);
  const volumes = get(primaryGroup, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  const dataVolumes = findAllDataVolumesInGroup(volumes);
  const mountPath = formSnapshot.dataMountPath || getVolumeMountPath(dataVolumes[0]) || DEFAULT_APP_DATA_MOUNT_PATH;
  const storageGroups = applyVolumeStatusStorageClassToGroups(
    detail,
    buildAppStorageGroupsFromDetailVolumes(dataVolumes, formSnapshot.inferenceFrameworkImage, formSnapshot.formsystem),
  );
  const systemDiskSizes = extractSystemDiskSizesFromGroups(spec);
  const nodeFields = parseDetailGroupsToNodeFormFields({
    runMode,
    headGroupSpec: spec.headGroupSpec,
    workerGroupSpecs: spec.workerGroupSpecs,
    commonEnv: Array.isArray(commonEnvFromAnnotation) ? commonEnvFromAnnotation : undefined,
  });

  const inferenceService = resolveInferenceServiceForForm(spec);
  const serviceMountGroupNames = resolveServiceMountGroupNamesForForm(spec);

  const formValues: ApplicationInstanceFormValues = applyMaasFormSnapshotToFormValues(
    {
      ...spec,
      namespace: detail.metadata?.namespace,
      workspace: spec.workspace || undefined,
      framework,
      runMode,
      runtime: normalizeMaasRuntime(spec.runtime) || spec.runtime,
      network: spec.network || { vpc: undefined },
      headGroupSpec: undefined,
      workerGroupSpecs: undefined,
      dataMountPath: mountPath,
      masterNodes: nodeFields.masterNodes ? stripInitCommandFromNodesJson(nodeFields.masterNodes) : undefined,
      workerNodes: nodeFields.workerNodes ? stripInitCommandFromNodesJson(nodeFields.workerNodes) : undefined,
      _inferenceSystem: nodeFields._inferenceSystem,
      _inferenceService: inferenceService,
      ...(serviceMountGroupNames ? { [SERVICE_MOUNT_GROUP_NAME_FIELD]: serviceMountGroupNames } : {}),
      ...buildSystemDiskFormFields(systemDiskSizes),
      ...(hydratedRegistry ? { registry: hydratedRegistry } : {}),
    },
    formSnapshot,
  );

  const syncedFormValues: ApplicationInstanceFormValues = {
    ...formValues,
    _inferenceService: syncInferenceServiceWithNetworkStatus(detail, inferenceService),
    ...(serviceMountGroupNames ? { [SERVICE_MOUNT_GROUP_NAME_FIELD]: serviceMountGroupNames } : {}),
  };

  const { _primaryDataVolume: _ignoredPrimaryDataVolume, ...formValuesWithoutPrimaryDataVolume } = syncedFormValues;

  const maasInferenceSource = resolveMaasInferenceSourceForForm(formValuesWithoutPrimaryDataVolume);

  if (maasInferenceSource === MaasInferenceSource.Custom) {
    const customModules = enrichAppInstanceSkuResourceModules(
      formValuesWithoutPrimaryDataVolume as Record<string, unknown>,
      formValuesWithoutPrimaryDataVolume.skuResourceModules as Record<string, boolean> | undefined,
    );
    return {
      ...formValuesWithoutPrimaryDataVolume,
      [MAAS_INFERENCE_SOURCE_FIELD]: maasInferenceSource,
      inferenceFrameworkImage: undefined,
      _containerDeployHealthCheck: undefined,
      _appStorageGroups: JSON.stringify(storageGroups),
      ...(customModules ? { skuResourceModules: customModules } : {}),
    };
  }

  const containerDeployConfig = resolveContainerDeployConfigFromFormValues(formValuesWithoutPrimaryDataVolume);
  let healthCheck = parseHealthCheckFromDeployConfig(containerDeployConfig?.healthCheck as Record<string, unknown> | undefined);
  if (!healthCheck) {
    const inferenceSystem = parseFormJsonField<Record<string, unknown>>(formValuesWithoutPrimaryDataVolume._inferenceSystem);
    healthCheck = healthCheckFromLivenessProbe(inferenceSystem?.livenessProbe as Record<string, unknown> | undefined);
  }

  const baseModules = formValuesWithoutPrimaryDataVolume.skuResourceModules as Record<string, boolean> | undefined;
  const withOptionalModules =
    enrichAppInstanceSkuResourceModules(formValuesWithoutPrimaryDataVolume as Record<string, unknown>, baseModules) ?? baseModules;
  const skuResourceModules = healthCheck
    ? {
        ...(withOptionalModules ?? {}),
        healthCheck: healthCheck.enabled === true,
      }
    : withOptionalModules;

  return {
    ...formValuesWithoutPrimaryDataVolume,
    [MAAS_INFERENCE_SOURCE_FIELD]: maasInferenceSource,
    _appStorageGroups: JSON.stringify(storageGroups),
    ...(healthCheck ? { _containerDeployHealthCheck: healthCheck as Record<string, unknown> } : {}),
    ...(skuResourceModules ? { skuResourceModules } : {}),
  };
}
